// src/components/ExploreVendors.js

import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { supabase } from '../lib/supabaseClient'
import { Link } from 'react-router-dom'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  StarIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  PencilIcon,
} from '@heroicons/react/24/outline'

export default function ExploreVendors() {
  const [vendors, setVendors] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // filters & pagination
  const [selectedCategories, setSelectedCategories] = useState([])
  const [locationQuery, setLocationQuery] = useState('')
  const [sortBy, setSortBy] = useState('newest')
  const [page, setPage] = useState(0)
  const perPage = 9   // show 9 cards per page

  // review modal state + form + OTP flow
  const [showReviewModal, setShowReviewModal] = useState(false)
  const [currentVendorId, setCurrentVendorId] = useState(null)
  const [reviewForm, setReviewForm] = useState({
    rating: 5,
    reviewer_name: '',
    reviewer_email: '',
    review_text: '',
  })
  const [step, setStep] = useState('input')              // 'input' or 'otp'
  const [reviewError, setReviewError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [reviewId, setReviewId] = useState(null)
  const [otp, setOtp] = useState('')

  // load vendors & categories
  useEffect(() => {
    async function loadData() {
      setLoading(true)
      const [
        { data: vData, error: vErr },
        { data: cData, error: cErr }
      ] = await Promise.all([
        supabase
          .from('vendors_with_reviews')
          .select(`
            id,
            business_name,
            service_categories,
            service_description,
            service_providing_locations,
            display_image_url,
            average_rating,
            reviews_count,
            inserted_at
          `)
          .order('inserted_at', { ascending: false }),
        supabase
          .from('categories')
          .select('categories')
          .order('id', { ascending: true })
      ])

      if (vErr || cErr) {
        setError((vErr || cErr).message)
      } else {
        setVendors(vData || [])
        setCategories((cData || []).map(c => c.categories))
      }
      setLoading(false)
    }
    loadData()
  }, [])

  // toggle a category chip
  const toggleCategory = useCallback(cat => {
    setSelectedCategories(prev =>
      prev.includes(cat)
        ? prev.filter(c => c !== cat)
        : [...prev, cat]
    )
    setPage(0)
  }, [])

  // filtering + sorting
  const filtered = useMemo(() => {
    let arr = vendors.filter(v => {
      const matchCat =
        selectedCategories.length === 0 ||
        selectedCategories.includes(v.service_categories)
      const matchSearch =
        !locationQuery ||
        v.service_providing_locations.toLowerCase().includes(locationQuery.toLowerCase()) ||
        v.business_name.toLowerCase().includes(locationQuery.toLowerCase())
      return matchCat && matchSearch
    })

    if (sortBy === 'rating_desc') {
      arr = [...arr].sort((a, b) => (b.average_rating || 0) - (a.average_rating || 0))
    } else if (sortBy === 'rating_asc') {
      arr = [...arr].sort((a, b) => (a.average_rating || 0) - (b.average_rating || 0))
    }
    return arr
  }, [vendors, selectedCategories, locationQuery, sortBy])

  // pagination
  const pages = Math.max(1, Math.ceil(filtered.length / perPage))
  const slice = useMemo(() => {
    const start = page * perPage
    return filtered.slice(start, start + perPage)
  }, [filtered, page, perPage])

  const handleNextPage = useCallback(() => {
    setPage(p => (p + 1) % pages)
  }, [pages])
  const handlePrevPage = useCallback(() => {
    setPage(p => (p - 1 + pages) % pages)
  }, [pages])

  // open the review/OTP modal
  const openReviewModal = vendorId => {
    setCurrentVendorId(vendorId)
    setReviewForm({
      rating: 5,
      reviewer_name: '',
      reviewer_email: '',
      review_text: '',
    })
    setReviewError('')
    setOtp('')
    setStep('input')
    setReviewId(null)
    setShowReviewModal(true)
  }

  const handleReviewChange = e => {
    const { name, value } = e.target
    setReviewForm(f => ({ ...f, [name]: value }))
  }

  // Step 1: insert/update then send OTP
  const handleReviewSubmit = async e => {
    e.preventDefault()
    setReviewError('')
    const { rating, reviewer_name, reviewer_email, review_text } = reviewForm
    if (!reviewer_name || !reviewer_email || !review_text) {
      setReviewError('Please fill in all fields.')
      return
    }
    setSubmitting(true)

    // check existing
    const { data: existing, error: fetchErr } = await supabase
      .from('reviews')
      .select('id')
      .eq('vendor_id', currentVendorId)
      .eq('reviewer_email', reviewer_email)
      .maybeSingle()
    if (fetchErr) {
      setSubmitting(false)
      setReviewError('Error checking existing review.')
      return
    }

    let rid
    if (existing) {
      // update existing
      const { error: updErr } = await supabase
        .from('reviews')
        .update({
          reviewer_name,
          review_text,
          rating,
          verify_code: null,
          verify_sent_at: null,
          is_verified: false,
        })
        .eq('id', existing.id)
      if (updErr) {
        setSubmitting(false)
        setReviewError('Failed to update your review.')
        return
      }
      rid = existing.id
    } else {
      // insert new
      const { data: inserted, error: insErr } = await supabase
        .from('reviews')
        .insert([{ vendor_id: currentVendorId, reviewer_name, reviewer_email, review_text, rating }])
        .select('id')
        .single()
      if (insErr || !inserted) {
        setSubmitting(false)
        setReviewError('Failed to submit your review.')
        return
      }
      rid = inserted.id
    }

    setReviewId(rid)

    // send OTP
    const { error: fnError } = await supabase.functions.invoke('sendReviewOtp', {
      body: JSON.stringify({ review_id: rid, email: reviewer_email }),
    })
    setSubmitting(false)
    if (fnError) {
      setReviewError('Failed to send verification code.')
      return
    }
    setStep('otp')
  }

  // Step 2: verify OTP
  const handleVerifyOtp = async e => {
    e.preventDefault()
    setReviewError('')
    if (!/^\d{6}$/.test(otp)) {
      setReviewError('Enter a 6-digit code.')
      return
    }
    setSubmitting(true)
    const { data: raw, error } = await supabase.functions.invoke('verifyReviewOtp', {
      body: JSON.stringify({ review_id: reviewId, code: otp }),
    })
    setSubmitting(false)
    if (error) {
      setReviewError(error.message || 'Verification failed.')
      return
    }
    const result = JSON.parse(raw)
    if (result.verified) {
      setShowReviewModal(false)
      // refresh this vendor’s metrics
      const { data: updated } = await supabase
        .from('vendors_with_reviews')
        .select('id,average_rating,reviews_count')
        .eq('id', currentVendorId)
        .single()
      setVendors(vs =>
        vs.map(v => v.id === currentVendorId
          ? { ...v, average_rating: updated.average_rating, reviews_count: updated.reviews_count }
          : v
        )
      )
    } else {
      setReviewError(result.error || 'Invalid or expired code.')
    }
  }

  if (loading) return <p className="text-center py-20">Loading…</p>
  if (error)   return <p className="text-center py-20 text-red-600">{error}</p>

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-20 py-16">
      <h1 className="text-3xl font-bold text-[#7C2D12] mb-6">Explore Vendors</h1>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-8 md:sticky md:top-16 md:z-10">
        <div className="flex items-center mb-4 text-gray-700">
          <MagnifyingGlassIcon className="w-6 h-6 mr-2" />
          <h3 className="font-semibold text-lg">Filters</h3>
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => toggleCategory(cat)}
              className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm transition
                ${selectedCategories.includes(cat)
                  ? 'bg-orange-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-orange-100 hover:text-orange-600'}
              `}
            >
              {cat}
              {selectedCategories.includes(cat) && <XMarkIcon className="w-4 h-4" />}
            </button>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row items-end gap-4">
          <div className="relative flex-grow">
            <input
              type="text"
              value={locationQuery}
              onChange={e => { setLocationQuery(e.target.value); setPage(0) }}
              placeholder="Search by location / Vendor name"
              className="pl-10 pr-10 border px-4 py-2 rounded-md focus:ring-2 focus:ring-orange-400 w-full"
            />
            <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"/>
            {locationQuery && (
              <button
                onClick={() => { setLocationQuery(''); setPage(0) }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                aria-label="Clear location"
              >
                <XMarkIcon className="w-5 h-5"/>
              </button>
            )}
          </div>
          <select
            value={sortBy}
            onChange={e => { setSortBy(e.target.value); setPage(0) }}
            className="border px-4 py-2 rounded-md focus:ring-2 focus:ring-orange-400"
          >
            <option value="newest">Newest</option>
            <option value="rating_desc">Rating: High to Low</option>
            <option value="rating_asc">Rating: Low to High</option>
          </select>
        </div>
      </div>

      {/* Vendor Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
        {slice.map(v => {
          const imgSrc = v.display_image_url?.trim() || '/assets/default-profile.png'
          return (
            <div key={v.id} className="bg-white p-6 rounded-lg shadow flex flex-col hover:-translate-y-1 hover:shadow-lg transition">
              {v.reviews_count > 0 && (
                <div className="flex items-center mb-3">
                  {Array.from({ length: 5 }, (_, i) => (
                    <StarIcon
                      key={i}
                      className={`w-5 h-5 ${i < Math.round(v.average_rating)
                        ? 'text-orange-500'
                        : 'text-gray-300'}`}
                    />
                  ))}
                  <span className="ml-2 text-sm text-gray-600">
                    {v.average_rating.toFixed(1)} ({v.reviews_count})
                  </span>
                </div>
              )}
              <img src={imgSrc} alt={v.business_name} className="w-24 h-24 rounded-full object-cover mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-center mb-1">{v.business_name}</h2>
              <span className="bg-orange-100 text-orange-600 px-3 py-1 rounded-full text-sm flex justify-center mb-4">
                {v.service_categories}
              </span>
              <p className="text-gray-600 flex-grow mb-4">{v.service_description}</p>
              <p className="text-gray-500 text-sm mb-6">Serving: {v.service_providing_locations}</p>
              <div className="mt-auto flex justify-between">
                <Link
                  to={`/vendors/${v.id}`}
                  className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-md transition"
                >
                  Details
                </Link>
                <button
                  onClick={() => openReviewModal(v.id)}
                  className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-md transition flex items-center gap-1"
                >
                  <PencilIcon className="w-5 h-5" /> Write a Review
                </button>
              </div>
            </div>
          )
        })}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-center gap-4 mt-8">
        <button onClick={handlePrevPage} disabled={pages <= 1} className="p-2 bg-white rounded-full shadow hover:bg-gray-50 disabled:opacity-30">
          <ChevronLeftIcon className="w-6 h-6 text-[#7C2D12]" />
        </button>
        <span className="text-gray-600">Page {page + 1} of {pages}</span>
        <button onClick={handleNextPage} disabled={pages <= 1} className="p-2 bg-white rounded-full shadow hover:bg-gray-50 disabled:opacity-30">
          <ChevronRightIcon className="w-6 h-6 text-[#7C2D12]" />
        </button>
      </div>

      {/* Review / OTP Modal */}
      {showReviewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full">
            {step === 'input' ? (
              <>
                <h2 className="text-xl font-bold mb-4">Write a Review</h2>
                {reviewError && <p className="text-red-600 mb-2">{reviewError}</p>}
                <form onSubmit={handleReviewSubmit} className="space-y-4">
                  <div>
                    <label className="block font-medium">Rating</label>
                    <div className="flex gap-2 mt-1">
                      {[1,2,3,4,5].map(n => (
                        <button
                          key={n}
                          type="button"
                          onClick={() => setReviewForm(f => ({ ...f, rating: n }))}
                          className={reviewForm.rating >= n ? 'text-orange-500' : 'text-gray-300'}
                        >
                          <StarIcon className="w-6 h-6" />
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block font-medium">Name</label>
                    <input
                      name="reviewer_name"
                      value={reviewForm.reviewer_name}
                      onChange={handleReviewChange}
                      required
                      className="mt-1 block w-full px-3 py-2 border rounded focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  <div>
                    <label className="block font-medium">Email</label>
                    <input
                      type="email"
                      name="reviewer_email"
                      value={reviewForm.reviewer_email}
                      onChange={handleReviewChange}
                      required
                      className="mt-1 block w-full px-3 py-2 border rounded focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  <div>
                    <label className="block font-medium">Review</label>
                    <textarea
                      name="review_text"
                      rows={4}
                      value={reviewForm.review_text}
                      onChange={handleReviewChange}
                      required
                      className="mt-1 block w-full px-3 py-2 border rounded focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setShowReviewModal(false)}
                      className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="px-4 py-2 rounded bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50"
                    >
                      {submitting ? 'Sending OTP…' : 'Submit & Verify Email'}
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <>
                <h2 className="text-xl font-bold mb-4">Enter Verification Code</h2>
                {reviewError && <p className="text-red-600 mb-2">{reviewError}</p>}
                <form onSubmit={handleVerifyOtp} className="space-y-4">
                  <input
                    value={otp}
                    onChange={e => setOtp(e.target.value)}
                    placeholder="123456"
                    className="w-full border p-2 rounded"
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setShowReviewModal(false)}
                      className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                    >
                      {submitting ? 'Verifying…' : 'Verify & Publish Review'}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}

    </div>
  )
}